import { useEffect, useState } from 'react';
import { isEntry, layoutDay } from './programModel';

// The program on the day of the event (the "live" view of the public page): which entries run now and
// where "now" is on the timeline. Program times are wall-clock times at the venue, so "now" is read in
// the event's time zone (program.event.timeZone, Europe/Vienna by default), whatever the device's zone.

export const VENUE_TIME_ZONE = 'Europe/Vienna';

// The anchor of an event page's address that makes its program scroll to what runs now once it is
// shown (ProgramSectionView): where the other pages' "Live" button (LiveProgramButton) leads.
export const LIVE_HASH = '#live';
const DAY_END = 24 * 60;

// "09:00:00" or "09:00" -> 540; null without a time.
export const minutesOf = (t) => {
  if (!t) return null;
  const [h, m] = String(t).split(':').map(Number);
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
};

const partsAt = (timeZone, date) => new Intl.DateTimeFormat('en-CA', {
  timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
}).formatToParts(date);

// The date ("YYYY-MM-DD") and the minute of the day at the venue at `date`.
export const venueNow = (timeZone, date = new Date()) => {
  let parts;
  try {
    parts = partsAt(timeZone || VENUE_TIME_ZONE, date);
  } catch (e) {
    parts = partsAt(VENUE_TIME_ZONE, date); // an unknown zone name
  }
  const get = (type) => parts.find((p) => p.type === type)?.value;
  return {
    day: `${get('year')}-${get('month')}-${get('day')}`,
    minutes: ((Number(get('hour')) % 24) * 60) + Number(get('minute')),
  };
};

// The venue's date and minute, recomputed at every full minute (and when the tab is shown again, as a
// hidden tab's timers are slowed down); null while `enabled` is false (the print copy).
export const useVenueNow = (timeZone, enabled = true) => {
  const [now, setNow] = useState(() => (enabled ? venueNow(timeZone) : null));
  useEffect(() => {
    if (!enabled) {
      setNow(null);
      return undefined;
    }
    let timer;
    const tick = () => {
      clearTimeout(timer);
      setNow(venueNow(timeZone));
      timer = setTimeout(tick, 60000 - (Date.now() % 60000));
    };
    const onVisible = () => { if (document.visibilityState === 'visible') tick(); };
    tick();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [timeZone, enabled]);
  return now;
};

// Where an entry ends: its own end time, else the next later start among its siblings, else where its
// parent ends (the end of the day at the top level).
const endOf = (entry, siblings, parentEnd) => {
  const own = minutesOf(entry.endTime);
  if (own != null) return own;
  const start = minutesOf(entry.startTime);
  const later = siblings.map((s) => minutesOf(s.startTime)).filter((m) => m != null && start != null && m > start);
  return later.length > 0 ? Math.min(...later) : parentEnd;
};

// The live state of one day at `minutes` past midnight (venue time):
//   running   ids of the entries with their own start time that run now (start <= now < end), at every
//             depth; a child without a time of its own inherits its block's and is not marked itself
//   past      ids of the entries with their own start time that have ended (end <= now), the same way
//   line      { slotIndex, fraction }: the slot of layoutDay(program, day).slots that holds "now" and how
//             far through it (0..1), a slot lasting until the next slot starts (the last one until its
//             latest end, `start` and `end` in minutes); before the first slot the top of the first one,
//             after the last slot null
//   passed    how many slots of the day lie wholly before now (all of them after the last slot)
export const liveDay = (program, day, minutes) => {
  const { slots } = layoutDay(program, day);
  const running = new Set();
  const past = new Set();
  const walk = (nodes, parentEnd) => {
    const entries = (nodes || []).filter(isEntry);
    entries.forEach((e) => {
      const start = minutesOf(e.startTime);
      const end = start != null ? endOf(e, entries, parentEnd) : parentEnd;
      if (start != null && start <= minutes && minutes < end) running.add(e.id);
      if (start != null && end <= minutes) past.add(e.id);
      walk(e.children, end);
    });
  };
  const top = slots.flatMap((s) => s.entries);
  walk(top, DAY_END);

  let line = null;
  let passed = 0;
  if (slots.length > 0) {
    const starts = slots.map((s) => minutesOf(s.time));
    const ends = slots.map((s, i) => (i + 1 < slots.length
      ? starts[i + 1]
      : Math.max(...s.entries.map((e) => endOf(e, top, DAY_END)))));
    if (minutes < starts[0]) line = { slotIndex: 0, fraction: 0, start: starts[0], end: ends[0] };
    else {
      const i = starts.findIndex((s, k) => s <= minutes && minutes < ends[k]);
      passed = i >= 0 ? i : slots.length;
      if (i >= 0) {
        line = { slotIndex: i, fraction: (minutes - starts[i]) / Math.max(ends[i] - starts[i], 1), start: starts[i], end: ends[i] };
      }
    }
  }
  return { running, past, line, passed };
};

// Where "now" lies between timed edges of the rendered program: `points` are { minute, y } (a timed
// entry's top at its start, its bottom at its end). The last point at or before now and the first one
// after it, read in order of time and then of height, are interpolated; null without both.
export const nowOffset = (points, minutes) => {
  const sorted = points.slice().sort((a, b) => a.minute - b.minute || a.y - b.y);
  let before = null;
  let after = null;
  sorted.forEach((p) => {
    if (p.minute <= minutes) before = p;
    else if (!after && (!before || p.y >= before.y)) after = p;
  });
  if (!before || !after) return null;
  return before.y + ((after.y - before.y) * (minutes - before.minute)) / Math.max(after.minute - before.minute, 1);
};

const PHONE = '(max-width: 768px)';

// The height (px from the slot's top) of the now line `lineEl` (a child of its slot), from where the
// timed entries are drawn; null when nothing is laid out (tests) - the line then keeps its share of the
// slot. Of parallel blocks the running one with the most timed parts is read. Wide screens interpolate
// between the entries' edges, so the line stands at the height of the talk that runs; on phones, where
// only its label shows (over the cards), it stands in the gap above the innermost running entry (above
// the block when no part of it runs, its own top before the first slot), so it never covers text.
export const placeNowLine = (lineEl, minutes, line) => {
  const slot = lineEl?.parentElement;
  if (!slot || !slot.offsetHeight) return null;
  const top = slot.getBoundingClientRect().top;
  const blocks = [...slot.querySelectorAll('article[data-start]')];
  if (blocks.length === 0) return null;
  const timedIn = (b) => b.querySelectorAll('[data-start]').length;
  const running = blocks.filter((b) => b.getAttribute('aria-current') === 'time');
  const block = (running.length > 0 ? running : blocks).reduce((a, b) => (timedIn(b) > timedIn(a) ? b : a));
  const timed = [block, ...block.querySelectorAll('[data-start]')];

  const phone = typeof window.matchMedia === 'function' && window.matchMedia(PHONE).matches;
  if (phone) {
    const inner = timed.filter((el) => el.getAttribute('aria-current') === 'time').pop();
    if (!inner || inner === block) return -8;
    return inner.getBoundingClientRect().top - top - 1;
  }

  const points = [{ minute: line.end, y: slot.offsetHeight }];
  timed.forEach((el) => {
    const rect = el.getBoundingClientRect();
    const start = Number(el.dataset.start);
    points.push({ minute: start, y: rect.top - top });
    if (el.dataset.end) points.push({ minute: Number(el.dataset.end), y: rect.bottom - top });
  });
  if (minutes < line.start) return 0;
  return nowOffset(points, minutes);
};
